import React, { createContext, useContext, ReactNode } from 'react';
import { Grocery } from '../Clients/reminds';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClientContext } from './ApiClient.Context';

interface ContextType {
  data: Grocery[] | undefined;
  deleteGrocery: (id: number) => void;
}

const Context = createContext<ContextType | undefined>(undefined);

export const useGroceriesContext = () => {
  const context = useContext(Context);
  if (!context) {
    throw new Error('useGroceriesContext must be used within a GroceriesProvider');
  }
  return context;
};

interface Props {
  children: ReactNode;
}

export const GroceriesProvider: React.FC<Props> = ({ children }) => {
  const { groceriesClient } = useApiClientContext();
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['groceries'],
    queryFn: () => groceriesClient.getGroceries(),
  });

  const { mutate } = useMutation({
    mutationFn: (id: number) => groceriesClient.deleteGrocery(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groceries'] });
    },
    onError: error => {
      console.error('Error deleting grocery item:', error);
    },
  });

  return <Context.Provider value={{ data, deleteGrocery: mutate }}>{children}</Context.Provider>;
};
